import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Exam, ExamResult, UserResponse, LeaderboardEntry } from '../types';
import { COLORS } from '../constants';
import { supabase } from '../services/supabase';
import { toggleBookmark, fetchBookmarks, fetchExamLeaderboard, calculateRank, submitReport } from '../services/dataService';
import QuestionTextRenderer from './QuestionTextRenderer';

interface ResultViewProps {
  result: ExamResult;
  exam: Exam;
  responses: UserResponse[];
  onBack: () => void;
}

const ResultView: React.FC<ResultViewProps> = ({ result, exam, responses, onBack }) => {
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [userId, setUserId] = useState<string | null>(null);

  // Time spent analytics
  const correctTime = responses.filter(r => {
    const q = exam.questions.find(quest => quest.id === r.questionId);
    return q && r.selectedOptionId === q.correctOptionId;
  });
  const incorrectTime = responses.filter(r => {
    const q = exam.questions.find(quest => quest.id === r.questionId);
    return q && r.selectedOptionId !== null && r.selectedOptionId !== q.correctOptionId;
  });

  const avgCorrectTime = correctTime.length > 0 
    ? Math.round(correctTime.reduce((sum, r) => sum + (r.timeSpent || 0), 0) / correctTime.length) 
    : 0;

  const avgIncorrectTime = incorrectTime.length > 0
    ? Math.round(incorrectTime.reduce((sum, r) => sum + (r.timeSpent || 0), 0) / incorrectTime.length)
    : 0;

  // Analytics State
  const [analytics, setAnalytics] = useState<{ rank: number, percentile: number, total: number } | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // Reporting State
  const [reportingQuestionId, setReportingQuestionId] = useState<string | null>(null);
  const [reportType, setReportType] = useState('wrong_answer');
  const [reportDesc, setReportDesc] = useState('');

  useEffect(() => {
    const init = async () => {
      let currentUserId = null;
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          currentUserId = session.user.id;
          setUserId(currentUserId);
          const bookmarks = await fetchBookmarks(session.user.id);
          setBookmarkedIds(new Set(bookmarks.map(b => b.questionId)));
        }
      }

      // Fetch Analytics & Leaderboard
      try {
        const [stats, lbData] = await Promise.all([
          calculateRank(exam.id, result.score),
          fetchExamLeaderboard(exam.id)
        ]);
        setAnalytics(stats);
        setLeaderboard(lbData);
      } catch (err) {
        console.error("Analytics load failed", err);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    init();
  }, [exam.id, result.score]);

  const handleBookmark = async (questionId: string) => {
    if (!userId) {
      alert("Please login to bookmark questions.");
      return;
    }
    const question = exam.questions.find(q => q.id === questionId);
    if (!question) return;

    const isAdded = await toggleBookmark(userId, question, exam.id);
    setBookmarkedIds(prev => {
      const next = new Set(prev);
      if (isAdded) next.add(questionId);
      else next.delete(questionId);
      return next;
    });
  };

  const handleSubmitReport = async () => {
    if (!userId || !reportingQuestionId) return;

    const success = await submitReport(userId, {
      questionId: reportingQuestionId,
      examId: exam.id,
      issueType: reportType as any,
      description: reportDesc
    });

    if (success) {
      alert("Thank you! Report submitted successfully.");
      setReportingQuestionId(null);
      setReportDesc('');
    } else {
      alert("Failed to submit report. Please try again.");
    }
  };

  const data = [
    { name: 'Correct', value: result.correctCount, color: COLORS.success },
    { name: 'Wrong', value: result.wrongCount, color: COLORS.danger },
    { name: 'Unattempted', value: result.unattemptedCount, color: '#94a3b8' },
  ];

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  return (
    <div className="min-h-screen bg-transparent p-6 md:p-12">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Test Result</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">{exam.title}</p>
          </div>
          <button onClick={onBack} className="px-6 py-2 bg-slate-900 dark:bg-slate-800 dark:hover:bg-slate-700 hover:bg-slate-800 text-white rounded transition-colors">
            Back to Dashboard
          </button>
        </div>

        {/* Top Grid: Performance & Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Score Card */}
          <div className="bg-white dark:bg-slate-800/80 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 lg:col-span-2">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-6">Performance Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">Total Score</div>
                <div className="text-3xl font-bold text-blue-900 dark:text-blue-200">{result.score.toFixed(2)} <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/ {exam.totalQuestions * exam.positiveMarks}</span></div>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
                <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">Accuracy</div>
                <div className="text-3xl font-bold text-green-900 dark:text-green-200">{result.accuracy.toFixed(1)}%</div>
              </div>
              <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">Rank</div>
                <div className="text-3xl font-bold text-purple-900 dark:text-purple-200">
                  {loadingAnalytics ? <i className="fas fa-circle-notch fa-spin text-sm"></i> : `#${analytics?.rank || '-'}`}
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400 block">out of {analytics?.total || '-'}</span>
                </div>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
                <div className="text-sm text-orange-600 dark:text-orange-400 font-medium mb-1">Percentile</div>
                <div className="text-3xl font-bold text-orange-900 dark:text-blue-200">
                  {loadingAnalytics ? <i className="fas fa-circle-notch fa-spin text-sm"></i> : `${analytics?.percentile.toFixed(1)}%`}
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-around border-t border-slate-100 dark:border-slate-700 pt-6">
              <div className="w-64 h-64 relative">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-2">Breakdown</h4>
                {data.map((item) => (
                  <div key={item.name} className="flex items-center justify-between w-48">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full mr-3" style={{ backgroundColor: item.color }}></div>
                      <span className="text-slate-600 dark:text-slate-400">{item.name}</span>
                    </div>
                    <span className="font-bold text-slate-900 dark:text-white">{item.value}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between w-48 pt-2 border-t border-slate-100 dark:border-slate-700 mt-2">
                  <span className="text-slate-600 dark:text-slate-400 text-sm">Total Time</span>
                  <span className="font-bold text-slate-900 dark:text-white text-sm">{formatTime(result.timeTaken)}</span>
                </div>
                <div className="flex items-center justify-between w-48 text-[11px] mt-1">
                  <span className="text-green-600 dark:text-green-400 font-medium">Avg Correct Time:</span>
                  <span className="font-bold text-green-700 dark:text-green-300">{avgCorrectTime}s</span>
                </div>
                <div className="flex items-center justify-between w-48 text-[11px] mt-0.5">
                  <span className="text-red-600 dark:text-red-400 font-medium">Avg Wrong Time:</span>
                  <span className="font-bold text-red-700 dark:text-red-300">{avgIncorrectTime}s</span>
                </div>
              </div>
            </div>
          </div>

          {/* Leaderboard Card */}
          <div className="bg-white dark:bg-slate-800/80 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center">
              <i className="fas fa-trophy text-yellow-500 mr-2"></i> Leaderboard
            </h2>

            {loadingAnalytics ? (
              <div className="flex justify-center items-center h-48 text-slate-400 dark:text-slate-500">
                <i className="fas fa-circle-notch fa-spin mr-2"></i> Loading ranks...
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center text-slate-400 dark:text-slate-500 py-12">
                <p>Be the first to take this test!</p>
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400">
                      <th className="pb-2 text-left pl-2">Rank</th>
                      <th className="pb-2 text-left">Name</th>
                      <th className="pb-2 text-right pr-2">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
                    {leaderboard.map((entry, idx) => (
                      <tr key={idx} className={entry.userId === userId ? "bg-orange-50 dark:bg-orange-950/20" : ""}>
                        <td className="py-3 pl-2 font-medium text-slate-900 dark:text-slate-300">
                          {entry.rank === 1 && <i className="fas fa-crown text-yellow-500 mr-1"></i>}
                          {entry.rank === 2 && <i className="fas fa-medal text-slate-400 mr-1"></i>}
                          {entry.rank === 3 && <i className="fas fa-medal text-orange-700 mr-1"></i>}
                          #{entry.rank}
                        </td>
                        <td className="py-3 text-slate-800 dark:text-slate-300">
                          {entry.userId === userId ? <span className="font-bold text-orange-700 dark:text-orange-400">You</span> : entry.name}
                        </td>
                        <td className="py-3 text-right pr-2 font-bold text-slate-700 dark:text-slate-300">
                          {entry.score.toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Solutions Preview */}
        <div className="bg-white dark:bg-slate-800/80 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <h3 className="font-semibold text-slate-800 dark:text-white">Question Review</h3>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {responses.map((resp, idx) => {
              const question = exam.questions.find(q => q.id === resp.questionId)!;
              const isCorrect = resp.selectedOptionId === question.correctOptionId;
              const isSkipped = resp.selectedOptionId === null;
              const isBookmarked = bookmarkedIds.has(question.id);

              return (
                <div key={resp.questionId} className="p-6">
                  <div className="flex items-start mb-4">
                    <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold mr-4
                        ${isCorrect ? 'bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400' : isSkipped ? 'bg-slate-100 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400' : 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400'}
                      `}>
                      {idx + 1}
                    </span>
                    <div className="flex-grow">
                      <div className="flex justify-between items-start">
                        <div className="text-slate-800 dark:text-slate-100 font-medium mb-2 pr-4">
                          <QuestionTextRenderer text={question.text} />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleBookmark(question.id)}
                            className={`text-lg transition-colors w-8 h-8 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${isBookmarked ? 'text-orange-500' : 'text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-300'}`}
                            title={isBookmarked ? "Remove Bookmark" : "Bookmark Question"}
                          >
                            <i className={`${isBookmarked ? 'fas' : 'far'} fa-bookmark`}></i>
                          </button>
                          <button
                            onClick={() => setReportingQuestionId(question.id)}
                            className="text-lg text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition-colors w-8 h-8 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                            title="Report Issue"
                          >
                            <i className="far fa-flag"></i>
                          </button>
                        </div>
                      </div>

                      {/* Time Spent and Performance Badges */}
                      <div className="flex flex-wrap gap-2 mb-3 mt-1">
                        <span className="inline-flex items-center text-xs bg-slate-100 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400 px-2.5 py-1 rounded font-medium">
                          <i className="far fa-clock mr-1.5 text-slate-400 dark:text-slate-500"></i> Time Spent: {resp.timeSpent || 0}s
                        </span>
                        {!isCorrect && !isSkipped && (resp.timeSpent || 0) > 120 && (
                          <span className="inline-flex items-center text-xs bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/30 px-2.5 py-1 rounded font-medium">
                            <i className="fas fa-hourglass-half mr-1.5 text-red-400"></i> Time Sink
                          </span>
                        )}
                        {!isSkipped && (resp.timeSpent || 0) < 5 && (
                          <span className="inline-flex items-center text-xs bg-sky-50 dark:bg-sky-950/20 text-sky-600 dark:text-sky-400 border border-sky-100 dark:border-sky-900/30 px-2.5 py-1 rounded font-medium">
                            <i className="fas fa-bolt mr-1.5 text-sky-400"></i> Rapid Answer
                          </span>
                        )}
                      </div>

                      {/* Result View Image Support */}
                      {question.imageUrl && (
                        <div className="mb-4">
                          <img
                            src={question.imageUrl}
                            alt="Question Diagram"
                            className="max-w-full max-h-64 rounded border border-slate-200 dark:border-slate-700 object-contain"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {question.options.map(opt => (
                          <div key={opt.id} className={`p-3 rounded border text-sm transition-colors
                              ${opt.id === question.correctOptionId ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900/30 text-green-900 dark:text-green-200' :
                              (resp.selectedOptionId === opt.id && !isCorrect) ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/30 text-red-900 dark:text-red-200' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'}
                            `}>
                            <span className="font-bold mr-2">{opt.id}.</span> {opt.text}
                            {opt.id === question.correctOptionId && <i className="fas fa-check text-green-600 dark:text-green-400 float-right"></i>}
                            {resp.selectedOptionId === opt.id && !isCorrect && <i className="fas fa-times text-red-600 dark:text-red-400 float-right"></i>}
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-900/30 rounded text-sm text-slate-600 dark:text-slate-400 border border-slate-100 dark:border-slate-800">
                        <span className="font-semibold text-slate-800 dark:text-slate-200">Explanation:</span> {question.explanation}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {reportingQuestionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700">
            <div className="p-4 bg-red-50 dark:bg-red-950/30 border-b border-red-100 dark:border-red-900/20 flex justify-between items-center">
              <h3 className="font-bold text-red-800 dark:text-red-400"><i className="fas fa-exclamation-triangle mr-2"></i> Report Question</h3>
              <button onClick={() => setReportingQuestionId(null)} className="text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Issue Type</label>
                <select
                  value={reportType}
                  onChange={e => setReportType(e.target.value)}
                  className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-red-500 outline-none"
                >
                  <option value="wrong_answer">Wrong Answer/Option</option>
                  <option value="typo">Typo/Grammar Error</option>
                  <option value="explanation_issue">Bad/Missing Explanation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
                <textarea
                  value={reportDesc}
                  onChange={e => setReportDesc(e.target.value)}
                  rows={4}
                  className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-red-500 outline-none resize-none"
                  placeholder="Describe the error in detail..."
                ></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setReportingQuestionId(null)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReport}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 shadow-sm"
                >
                  Submit Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultView;